import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { clearOnboardingStatus, setOnboardingStatus } from '../onboarding/storage.js';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { FormInput } from '../components/FormControls.jsx';
import { FormRichTextarea } from '../components/FormRichTextarea.jsx';
import { NotificationPreferencesSection } from '../components/NotificationPreferencesSection.jsx';
import { applyDocumentLocale, SUPPORTED_UI_LOCALES, UI_LOCALE_LABELS, normalizeUiLocale } from '../i18n/locales.js';
import { profileUi } from '../i18n/profileUi.js';
import { t } from '../i18n/translate.js';
import { ROLE_LABELS } from '../utils/roles.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';
import { TwoFactorProfileSection } from '../components/TwoFactorProfileSection.jsx';

export function ProfilePage({ backTo = '/admin' }) {
  const { user, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [uiLocale, setUiLocale] = useState(normalizeUiLocale(user?.ui_locale));
  const [onboardingEnabled, setOnboardingEnabled] = useState(user?.onboarding_enabled !== false);
  const [emailSignature, setEmailSignature] = useState(user?.email_signature || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const locale = normalizeUiLocale(uiLocale);

  useEffect(() => {
    apiJson('/api/profile')
      .then((data) => {
        setProfile(data);
        setName(data.name || '');
        setEmail(data.email || '');
        setUiLocale(normalizeUiLocale(data.ui_locale));
        setOnboardingEnabled(data.onboarding_enabled !== false);
        setEmailSignature(data.email_signature || '');
      })
      .catch(() => setProfile(null));
  }, []);

  useEffect(() => {
    applyDocumentLocale(uiLocale);
  }, [uiLocale]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    try {
      await apiJson('/api/profile', {
        method: 'PUT',
        body: { name, email, ui_locale: uiLocale, onboarding_enabled: onboardingEnabled, email_signature: emailSignature },
      });
      if (user?.id && user?.role) {
        if (onboardingEnabled) {
          clearOnboardingStatus(user.id, user.role);
        } else {
          setOnboardingStatus(user.id, user.role, 'dismissed');
        }
      }
      await refreshUser();
      setMsg(t(profileUi.profileUpdated, locale));
    } catch (err) {
      setError(err.message);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    try {
      await apiJson('/api/profile/password', {
        method: 'POST',
        body: { current_password: currentPassword, password, password_confirmation: password2 },
      });
      setCurrentPassword('');
      setPassword('');
      setPassword2('');
      setMsg(t(profileUi.passwordChanged, locale));
    } catch (err) {
      setError(err.message);
    }
  };

  const verifyEmail = async () => {
    try {
      await apiJson('/api/profile/verify-email', { method: 'POST' });
      await refreshUser();
      setMsg(t(profileUi.emailMarkedVerified, locale));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <p>
        <Link to={backTo}>{t(profileUi.back, locale)}</Link>
      </p>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">{t(profileUi.title, locale)}</h2>
      <div className="auth-card flat" style={{ marginBottom: '1rem' }}>
        <p className="muted small" style={{ margin: 0 }}>
          {t(profileUi.role, locale)}: <strong>{ROLE_LABELS[user?.role] || user?.role}</strong>
        </p>
        {profile?.organization?.name ? (
          <p className="muted small" style={{ margin: '0.5rem 0 0' }}>
            {t(profileUi.organization, locale)}: <strong>{profile.organization.name}</strong>
          </p>
        ) : null}
        {profile?.client?.name ? (
          <p className="muted small" style={{ margin: '0.5rem 0 0' }}>
            {t(profileUi.client, locale)}: <strong>{profile.client.name}</strong>
          </p>
        ) : null}
      </div>
      {error ? <p className="err">{error}</p> : null}
      {msg ? <p className="muted">{msg}</p> : null}
      {!user?.email_verified_at ? (
        <p>
          <button type="button" className="btn-secondary" onClick={() => void verifyEmail()}>
            {t(profileUi.markVerified, locale)}
          </button>
        </p>
      ) : (
        <p className="muted small">{t(profileUi.emailVerified, locale)}</p>
      )}
      <TwoFactorProfileSection locale={locale} />
      <form onSubmit={saveProfile} className="auth-card flat stack" data-tour="profile-details">
        <h3>{t(profileUi.details, locale)}</h3>
        <label className="field">
          <span>{t(profileUi.name, locale)}</span>
          <FormInput value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>{t(profileUi.email, locale)}</span>
          <FormInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field" data-tour="profile-language">
          <span>{t(profileUi.language, locale)}</span>
          <SearchableSelect
            className="form-control"
            value={uiLocale}
            onChange={(e) => setUiLocale(normalizeUiLocale(e.target.value))}
          >
            {SUPPORTED_UI_LOCALES.map((code) => (
              <option key={code} value={code}>
                {UI_LOCALE_LABELS[code]}
              </option>
            ))}
          </SearchableSelect>
          <span className="muted small">{t(profileUi.languageHint, locale)}</span>
        </label>
        <label className="field onboarding-profile-toggle" data-tour="profile-onboarding">
          <span className="checkbox-row">
            <input
              type="checkbox"
              checked={onboardingEnabled}
              onChange={(e) => setOnboardingEnabled(e.target.checked)}
            />
            <span>{t(profileUi.onboardingEnabled, locale)}</span>
          </span>
          <span className="muted small">{t(profileUi.onboardingEnabledHint, locale)}</span>
        </label>
        <FormRichTextarea
          label={t(profileUi.emailSignature, locale)}
          value={emailSignature}
          onChange={setEmailSignature}
          height={200}
        />
        <span className="muted small">{t(profileUi.emailSignatureHint, locale)}</span>
        <button type="submit" className="btn-primary">
          {t(profileUi.saveProfile, locale)}
        </button>
      </form>
      <form onSubmit={changePassword} className="auth-card flat stack" style={{ marginTop: '1rem' }} data-tour="profile-password">
        <h3>{t(profileUi.changePassword, locale)}</h3>
        <label className="field">
          <span>{t(profileUi.currentPassword, locale)}</span>
          <FormInput type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
        </label>
        <label className="field">
          <span>{t(profileUi.newPassword, locale)}</span>
          <FormInput type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <label className="field">
          <span>{t(profileUi.confirmPassword, locale)}</span>
          <FormInput type="password" value={password2} onChange={(e) => setPassword2(e.target.value)} />
        </label>
        <button type="submit" className="btn-secondary">
          {t(profileUi.updatePassword, locale)}
        </button>
      </form>
      <NotificationPreferencesSection dataTour="profile-notifications" />
    </div>
  );
}
